import React, { useState, useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getUserData } from '../../services/accessControlService';
import {
  FaCog,
  FaChartLine,
  FaBullhorn,
  FaUsers,
  FaRoute,
  FaPlay
} from 'react-icons/fa';
import PortalBackground from '../../components/PortalBackground';
import BBALoader from '../../components/BBALoader';

/**
 * Department hub — each department page combines that team's fulfillment and
 * templates. Visibility rules:
 *  - Owner: every department, including General.
 *  - Executive: their team's department + General.
 *  - Everyone else: their team's department only (sent straight there).
 */

/** Map an internal_users team value to a hub department key. */
const getTeamDepartment = (team) => {
  switch (team) {
    case 'Marketing':
      return 'marketing';
    case 'HR':
      return 'hr';
    default:
      return 'general';
  }
};

const DEPARTMENTS = [
  {
    key: 'general',
    name: 'General',
    icon: <FaCog />,
    color: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
    description: 'General fulfillment processes and procedures',
    path: '/crm/fulfillment/general'
  },
  {
    key: 'marketing',
    name: 'Marketing',
    icon: <FaBullhorn />,
    color: 'bg-orange-100 text-orange-600 dark:bg-orange-900/40 dark:text-orange-300',
    description: 'Marketing fulfillment, campaigns, and website plugins',
    path: '/crm/fulfillment/marketing'
  },
  {
    key: 'hr',
    name: 'HR',
    icon: <FaUsers />,
    color: 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900/40 dark:text-yellow-300',
    description: 'Human resources fulfillment processes',
    path: '/crm/fulfillment/hr'
  },
  // Cross-team pages live under the General umbrella for visibility purposes.
  {
    key: 'general',
    name: 'Breakthrough Sessions',
    icon: <FaChartLine />,
    color: 'bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-300',
    description: 'Breakthrough sessions fulfillment',
    path: '/crm/fulfillment/breakthroughsfufillment'
  },
  {
    key: 'general',
    name: 'Client Roadmaps',
    icon: <FaRoute />,
    color: 'bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-300',
    description: 'Client roadmaps and planning',
    path: '/crm/fulfillment/clientroadmaps'
  },
  {
    key: 'general',
    name: 'Kickoff',
    icon: <FaPlay />,
    color: 'bg-green-100 text-green-600 dark:bg-green-900/40 dark:text-green-300',
    description: 'Client kickoff and onboarding',
    path: '/crm/fulfillment/kickoff'
  }
];

const Fulfillment = () => {
  const { currentUser } = useAuth();
  const [userRole, setUserRole] = useState(null);
  /** `undefined` = not loaded yet (avoid flashing all department cards). */
  const [userTeam, setUserTeam] = useState(undefined);

  useEffect(() => {
    const fetchUserRole = async () => {
      if (currentUser) {
        const userData = await getUserData(currentUser.uid);
        if (userData) {
          setUserRole(userData.role);
          setUserTeam(userData.data?.team ?? 'General');
        } else {
          setUserTeam('General');
        }
      } else {
        setUserTeam('General');
      }
    };
    fetchUserRole();
  }, [currentUser]);

  if (userTeam === undefined) {
    return (
      <div className="min-h-screen p-6 flex items-center justify-center relative">
        <PortalBackground />
        <div className="relative"><BBALoader size={48} /></div>
      </div>
    );
  }

  const teamDepartment = getTeamDepartment(userTeam);

  const visibleDepartments = DEPARTMENTS.filter((dept) => {
    if (userRole === 'Owner') return true;
    if (userRole === 'Executive') return dept.key === 'general' || dept.key === teamDepartment;
    return dept.key === teamDepartment;
  });

  // Members of a single-department team go straight to their department page.
  if (
    userRole !== 'Owner' &&
    userRole !== 'Executive' &&
    visibleDepartments.length === 1
  ) {
    return <Navigate to={visibleDepartments[0].path} replace />;
  }

  return (
    <div className="min-h-screen p-6 relative">
      {/* Same ambient BBA background as the Leads, Clients and Tasks pages */}
      <PortalBackground />
      <div className="relative max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
            Fulfillment
          </h1>
          <p className="mt-2 text-gray-500 dark:text-gray-400">
            Fulfillment and templates by department
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {visibleDepartments.map((dept) => (
            <Link key={dept.path} to={dept.path} className="group block">
              <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-10 min-h-[200px] flex flex-col items-center justify-center text-center transition-all duration-200 group-hover:shadow-md group-hover:-translate-y-0.5">
                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-5 transition-transform group-hover:scale-105 ${dept.color}`}>
                  {React.cloneElement(dept.icon, { className: 'text-3xl' })}
                </div>
                <h3 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
                  {dept.name}
                </h3>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 max-w-xs">
                  {dept.description}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Fulfillment;
